import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { takePendingConsent } from '../pages/auth/oauth-consent/oauth-consent-pending';

/** Total runtime of the login-success transition. The grain-pulse sweep
 *  uses a sin envelope so the peak sits at DURATION/2 — the navigation
 *  is scheduled there so the swap is fully masked. Tail is longer than
 *  the rise to give the dashboard a gentle fade-in. */
export const AUTH_TRANSITION_DURATION_MS = 1500;

/** Point inside the transition at which the router navigates to the
 *  dashboard. Anchored to the sin envelope's peak (DURATION/2) so the
 *  wave fully masks the route swap. */
export const AUTH_TRANSITION_NAV_AT_MS = AUTH_TRANSITION_DURATION_MS / 2;

export interface AuthTransitionRequest {
  /** Router URL to navigate to once the dissolve has masked the screen. */
  redirect: string;
}

/** Drives the post-login dissolve-and-reveal transition. The login
 *  component calls `start(redirect)` instead of routing directly; the
 *  AuthTransitionComponent listens on `request$`, orchestrates the
 *  grain-pulse + card dissolve, navigates at the peak, and fades the
 *  dashboard in behind the wave. */
@Injectable({ providedIn: 'root' })
export class AuthTransitionService {
  private readonly requestSubject = new Subject<AuthTransitionRequest>();
  readonly request$ = this.requestSubject.asObservable();

  start(redirect: string): void {
    // An AI agent's sign-in sent the person to the login first: go back to its
    // consent page instead of the dashboard (pages/auth/oauth-consent).
    this.requestSubject.next({ redirect: takePendingConsent() ?? redirect });
  }
}
